import type { RemoteTrack, Room } from 'livekit-client'
import type { AudioTelemetryBuffer } from './AudioTelemetryBuffer'
import {
  installLivekitLogForwarding,
  type LivekitLogSink,
} from '../../util/livekitLogForwarding'
import { getAudioSentryPort } from '../../util/audioSentryPort'

/**
 * AudioObservabilitySources — the noisy low-level context sources that feed the
 * ring: livekit-internal logs, remote-track mute, page lifecycle, and element
 * stall events.
 *
 * None of these emit telemetry on their own — they fire constantly during
 * healthy play. They write into the ~30s ring, so when the dead-air detector
 * confirms an episode the bundle explains *why* the client went deaf: a
 * livekit-internal warning, a browser-initiated remote mute, a page going
 * hidden, an `<audio>` element stalling.
 *
 * Kept as a sibling class the controller owns so the hot controller only
 * constructs and disposes it.
 */

/** The low-frequency media element events worth recording (never `timeupdate`). */
const MEDIA_EVENTS = ['stalled', 'waiting', 'suspend', 'pause'] as const

/**
 * `trackSubscribed` fires before the renderer calls `track.attach()`, so the
 * element list is still empty on the first pass and a single attempt would
 * silently register nothing. Retry on this schedule; attachElementListeners is
 * idempotent per element, so the later passes are no-ops once it has landed.
 */
const ELEMENT_ATTACH_RETRY_MS = [250, 1000, 3000] as const

export interface AudioObservabilitySourcesOptions {
  ring: AudioTelemetryBuffer
}

export class AudioObservabilitySources {
  private readonly ring: AudioTelemetryBuffer
  private unsubscribers: (() => void)[] = []
  private uninstallLogForwarding: (() => void) | null = null
  /**
   * Per-element cleanup for the attached media-event listeners, stamped with
   * the track that registered it.
   *
   * Owning the track is what makes unsubscribe cleanup reliable. Finding the
   * entries to drop by re-reading `track.attachedElements` assumed that list
   * still held the same elements at `trackUnsubscribed` time, but the renderer
   * detaches on its own schedule — a React unmount driven by state that lands
   * independently of this event. Whenever it got there first the list was
   * already empty, the lookup found nothing, and the listeners stayed attached
   * to a detached element until the room tore down.
   *
   * Identity is the track object, not `track.sid`, because the sid is optional
   * on the livekit type; the room emits the same instance to `trackSubscribed`
   * and `trackUnsubscribed`.
   */
  private readonly elementListeners = new Map<
    HTMLMediaElement,
    { track: RemoteTrack; cleanup: () => void }
  >()

  /** Pending attach retries, cleared on detach so none outlive the room. */
  private readonly attachRetryTimers = new Set<ReturnType<typeof setTimeout>>()

  /**
   * Tracks currently subscribed, so a pending retry can tell whether the track
   * it was armed for is still there.
   *
   * The retries stay armed for 3s and unsubscribe does not cancel them, so a
   * track that goes away inside that window — a mute/unmute republish, a peer
   * leaving, subscription churn on reconnect — used to get its listeners
   * re-registered by the next retry, after cleanup had already run. Those
   * entries then lived until {@link detach}, and the element kept writing
   * `audio_element` events into the ring under a sid that was no longer
   * subscribed: noise in the bundle the episode is read from.
   */
  private readonly subscribedTracks = new WeakSet<RemoteTrack>()

  constructor(options: AudioObservabilitySourcesOptions) {
    this.ring = options.ring
  }

  attach(room: Room): void {
    // livekit-client's own internal warnings/errors.
    const logSink: LivekitLogSink = {
      push: (s, e, d) => this.ring.push(s, e, d),
      breadcrumb: (m, d) => this.breadcrumb(m, d),
    }
    this.uninstallLogForwarding = installLivekitLogForwarding(logSink)

    // Browser-initiated remote-track mute/unmute (not our own local mic).
    const recordMuteEvent =
      (event: 'mute' | 'unmute') =>
      (
        pub: { kind: string },
        participant: { identity: string; isLocal: boolean }
      ) => {
        if (participant.isLocal) return
        this.record('remote_track', event, {
          participant: participant.identity,
          kind: pub.kind,
        })
      }
    // Held in consts so the same references reach room.off below.
    const onMuted = recordMuteEvent('mute')
    const onUnmuted = recordMuteEvent('unmute')
    room.on('trackMuted', onMuted)
    room.on('trackUnmuted', onUnmuted)
    this.unsubscribers.push(() => {
      room.off('trackMuted', onMuted)
      room.off('trackUnmuted', onUnmuted)
    })

    // Element lifecycle on subscribed audio tracks.
    const onSubscribed = (track: RemoteTrack) => {
      if (track.kind.toString() !== 'audio') return
      this.subscribedTracks.add(track)
      this.attachElementListeners(track)
      for (const delay of ELEMENT_ATTACH_RETRY_MS) {
        const timer = setTimeout(() => {
          this.attachRetryTimers.delete(timer)
          if (!this.subscribedTracks.has(track)) return
          this.attachElementListeners(track)
        }, delay)
        this.attachRetryTimers.add(timer)
      }
    }
    const onUnsubscribed = (track: RemoteTrack) => {
      if (track.kind.toString() !== 'audio') return
      this.subscribedTracks.delete(track)
      this.detachElementListeners(track)
    }
    room.on('trackSubscribed', onSubscribed)
    room.on('trackUnsubscribed', onUnsubscribed)
    this.unsubscribers.push(() => {
      room.off('trackSubscribed', onSubscribed)
      room.off('trackUnsubscribed', onUnsubscribed)
    })

    // Page lifecycle / visibility (feeds the pageHidden signal).
    if (typeof document !== 'undefined') {
      const onVisibility = () => {
        const hidden = document.visibilityState === 'hidden'
        this.record('lifecycle', hidden ? 'hidden' : 'visible', {
          hidden: document.hidden,
        })
      }
      document.addEventListener('visibilitychange', onVisibility)
      this.unsubscribers.push(() =>
        document.removeEventListener('visibilitychange', onVisibility)
      )
    }
    if (typeof window !== 'undefined') {
      const onPageHide = () => this.record('lifecycle', 'pagehide', {})
      window.addEventListener('pagehide', onPageHide)
      this.unsubscribers.push(() =>
        window.removeEventListener('pagehide', onPageHide)
      )
    }
  }

  detach(): void {
    this.uninstallLogForwarding?.()
    this.uninstallLogForwarding = null
    for (const timer of this.attachRetryTimers) clearTimeout(timer)
    this.attachRetryTimers.clear()
    for (const unsub of this.unsubscribers) unsub()
    this.unsubscribers = []
    for (const { cleanup } of this.elementListeners.values()) cleanup()
    this.elementListeners.clear()
  }

  private attachElementListeners(track: RemoteTrack): void {
    for (const el of track.attachedElements ?? []) {
      if (this.elementListeners.has(el)) continue
      const removers = MEDIA_EVENTS.map((evt) => {
        const handler = () =>
          this.record('audio_element', evt, { sid: track.sid })
        el.addEventListener(evt, handler)
        return () => el.removeEventListener(evt, handler)
      })
      this.elementListeners.set(el, {
        track,
        cleanup: () => {
          for (const remove of removers) remove()
        },
      })
    }
  }

  /**
   * Drop every listener this track registered, found by owning track rather
   * than by re-reading its current element list — see {@link elementListeners}.
   */
  private detachElementListeners(track: RemoteTrack): void {
    for (const [el, entry] of this.elementListeners) {
      if (entry.track !== track) continue
      entry.cleanup()
      this.elementListeners.delete(el)
    }
  }

  /**
   * Record a context transition. Ring only — deliberately not a breadcrumb.
   *
   * These sources fire on every mute, every visibility change and every element
   * stall, so breadcrumbing each one would evict the rest of the 100-slot
   * global breadcrumb ring inside a single healthy meeting. That eviction is
   * the exact problem this ring exists to route around, and paying it twice
   * would also cost the console/fetch/navigation crumbs every *other* Sentry
   * issue depends on. The livekit log sink still breadcrumbs, because warn+ is
   * rare and useful to any error, not only a dead-air one.
   */
  private record(
    source: string,
    event: string,
    data: Record<string, unknown>
  ): void {
    this.ring.push(source, event, data)
  }

  private breadcrumb(message: string, data?: Record<string, unknown>): void {
    getAudioSentryPort()?.addBreadcrumb({
      category: 'audio',
      message,
      level: 'info',
      data,
    })
  }
}
